import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  CalendarRange,
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { SocialTrackerItem } from '@digital-twins/shared-types'
import Link from '@/routing/Link'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import {
  askAboutTrackerHref,
  formatSocialDateTime,
  SOCIAL_PLATFORM_LABELS,
} from './socialFormat'
import {
  isTrackerInProgress,
  isTrackerRetryable,
  STATUS_TONE_BADGE_VARIANT,
  trackerStatusTone,
} from './socialTrackerStatus'

interface SocialTrackerCardProps {
  tracker: SocialTrackerItem
  canManage: boolean
  isBusy: boolean
  onOpen: (token: string) => void
  onSync: (tracker: SocialTrackerItem) => void
  onTogglePause: (tracker: SocialTrackerItem) => void
  onEdit: (tracker: SocialTrackerItem) => void
  onDelete: (tracker: SocialTrackerItem) => void
  canAsk?: boolean
  /** Manage actions as buttons on the card instead of behind the ⋯ menu. */
  showActionsInline?: boolean
}

export function SocialTrackerCard(props: SocialTrackerCardProps) {
  const {
    tracker,
    canManage,
    isBusy,
    onOpen,
    onSync,
    onTogglePause,
    onEdit,
    onDelete,
    canAsk = true,
    showActionsInline = false,
  } = props

  const { t, i18n } = useTranslation('app')

  const testId = `social-tracker-card-${tracker.token}`
  const tone = trackerStatusTone(tracker.status)
  const isInProgress = isTrackerInProgress(tracker.status)
  const isRetryable = isTrackerRetryable(tracker.status)
  const isPaused = tracker.status === 'paused'
  const lastSynced = formatSocialDateTime(tracker.lastSyncedAt, i18n.language)
  const newestPost = formatSocialDateTime(tracker.newestPostAt, i18n.language)
  const askHref = askAboutTrackerHref(tracker)
  const askThisWeekHref = canAsk
    ? askAboutTrackerHref(tracker, {
        question: t('socialListening.askQuestion.thisWeek', {
          name: tracker.name,
        }),
        window: '7d',
      })
    : null

  const statusDetail =
    tracker.status === 'failed' && tracker.lastError
      ? tracker.lastError
      : tracker.status === 'over_budget'
        ? t('socialListening.statusHint.over_budget')
        : null

  const staleBanner = tracker.stale ? (
    <div
      className="flex items-center gap-2 rounded-lg bg-chart-warning/10 px-3 py-2 text-[13px] text-foreground"
      role="status"
      data-testid={`${testId}-stale`}
    >
      <AlertTriangle
        className="h-4 w-4 shrink-0 text-chart-warning"
        aria-hidden="true"
      />
      {lastSynced
        ? t('socialListening.stale', { date: lastSynced })
        : t('socialListening.staleNever')}
    </div>
  ) : null

  const retryButton =
    canManage && isRetryable ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        shape="pill"
        className="h-8 px-3 text-[13px]"
        disabled={isBusy}
        data-testid={`${testId}-retry`}
        onClick={() => onSync(tracker)}
      >
        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
        {t('socialListening.card.retry')}
      </Button>
    ) : null

  return (
    <article
      className="flex flex-col gap-3 rounded-2xl border border-border bg-card-inset p-5 dark:bg-card"
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
          <SocialPlatformIcon platform={tracker.platform} />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <Text
            as="span"
            size="xs"
            color="muted"
            className="block uppercase tracking-[0.04em]"
          >
            {SOCIAL_PLATFORM_LABELS[tracker.platform]} · {tracker.market} /{' '}
            {tracker.language.toUpperCase()}
          </Text>
          <Heading level={3} className="line-clamp-1 leading-snug">
            {tracker.name}
          </Heading>
          <Text
            size="sm"
            color="muted"
            className="line-clamp-1 text-[13px]"
            data-testid={`${testId}-keywords`}
          >
            {tracker.keywords.map((keyword) => `“${keyword}”`).join(', ')}
          </Text>
        </div>
      </div>

      {staleBanner}

      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant={STATUS_TONE_BADGE_VARIANT[tone]}
          className="gap-1.5 rounded-full px-2.5 py-0.5 text-[12px]"
          data-testid={`${testId}-status`}
          data-status={tracker.status}
        >
          {isInProgress ? (
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary"
              aria-hidden="true"
            />
          ) : null}
          {t(`socialListening.status.${tracker.status}`)}
        </Badge>
        {retryButton}
      </div>
      {statusDetail ? (
        <Text
          size="sm"
          color="muted"
          className="text-[13px]"
          data-testid={`${testId}-status-detail`}
        >
          {statusDetail}
        </Text>
      ) : null}

      {!tracker.lastSyncedAt && tracker.totalPosts === 0 ? (
        <Text
          size="sm"
          color="muted"
          data-testid={`${testId}-waiting-first-sync`}
        >
          {t('socialListening.card.waitingFirstSync')}
        </Text>
      ) : (
        <dl className="grid grid-cols-1 gap-1 text-[13px] text-muted-foreground">
          <div>
            {lastSynced
              ? t('socialListening.card.lastSync', { date: lastSynced })
              : t('socialListening.card.neverSynced')}
          </div>
          <div className="flex flex-wrap gap-x-3">
            <span>
              {newestPost
                ? t('socialListening.card.newestPost', { date: newestPost })
                : t('socialListening.card.noPosts')}
            </span>
            <span data-testid={`${testId}-posts-30d`}>
              {tracker.postsLast30Days === undefined
                ? t('socialListening.card.activityUnavailable')
                : t('socialListening.card.postsLast30Days', {
                    count: tracker.postsLast30Days,
                  })}
            </span>
          </div>
        </dl>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
        <Button
          type="button"
          variant="cta"
          size="sm"
          shape="pill"
          className="h-9 px-4"
          data-testid={`${testId}-open`}
          onClick={() => onOpen(tracker.token)}
        >
          {t('socialListening.card.open')}
        </Button>
        {!canAsk ? null : askHref ? (
          <Button
            asChild
            variant="outline"
            size="sm"
            shape="pill"
            className="h-9 px-4"
          >
            <Link href={askHref} data-testid={`${testId}-ask`}>
              {t('socialListening.card.ask')}
            </Link>
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            shape="pill"
            className="h-9 px-4"
            disabled
            title={t('socialListening.card.askUnavailable')}
            data-testid={`${testId}-ask`}
          >
            {t('socialListening.card.ask')}
          </Button>
        )}
        {showActionsInline && canManage ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="pill"
              className="h-9 px-4"
              disabled={isBusy || isInProgress || isPaused}
              data-testid={`${testId}-sync`}
              onClick={() => onSync(tracker)}
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              {isRetryable
                ? t('socialListening.card.retry')
                : t('socialListening.card.runNow')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="pill"
              className="h-9 px-4"
              disabled={isBusy}
              data-testid={`${testId}-pause`}
              onClick={() => onTogglePause(tracker)}
            >
              {isPaused ? (
                <Play className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <Pause className="mr-1.5 h-3.5 w-3.5" />
              )}
              {isPaused
                ? t('socialListening.card.resume')
                : t('socialListening.card.pause')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="pill"
              className="h-9 px-4"
              disabled={isBusy}
              data-testid={`${testId}-edit`}
              onClick={() => onEdit(tracker)}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {t('socialListening.card.edit')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="pill"
              className="h-9 px-4 text-destructive hover:text-destructive"
              disabled={isBusy}
              data-testid={`${testId}-delete`}
              onClick={() => onDelete(tracker)}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              {t('socialListening.card.delete')}
            </Button>
          </>
        ) : askThisWeekHref || canManage ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-auto h-9 w-9"
                aria-label={t('socialListening.card.menuAria', {
                  name: tracker.name,
                })}
                data-testid={`${testId}-menu`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[200px] rounded-xl"
            >
              {askThisWeekHref ? (
                <DropdownMenuItem asChild>
                  <Link
                    href={askThisWeekHref}
                    data-testid={`${testId}-ask-week`}
                  >
                    <CalendarRange className="mr-2 h-4 w-4" />
                    {t('socialListening.card.askThisWeek')}
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {canManage ? (
                <>
                  {askThisWeekHref ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuItem
                    disabled={isBusy || isInProgress || isPaused}
                    data-testid={`${testId}-sync`}
                    onClick={() => onSync(tracker)}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {isRetryable
                      ? t('socialListening.card.retry')
                      : t('socialListening.card.runNow')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={isBusy}
                    data-testid={`${testId}-pause`}
                    onClick={() => onTogglePause(tracker)}
                  >
                    {isPaused ? (
                      <Play className="mr-2 h-4 w-4" />
                    ) : (
                      <Pause className="mr-2 h-4 w-4" />
                    )}
                    {isPaused
                      ? t('socialListening.card.resume')
                      : t('socialListening.card.pause')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={isBusy}
                    data-testid={`${testId}-edit`}
                    onClick={() => onEdit(tracker)}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    {t('socialListening.card.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={isBusy}
                    className="text-destructive focus:text-destructive"
                    data-testid={`${testId}-delete`}
                    onClick={() => onDelete(tracker)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('socialListening.card.delete')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </article>
  )
}
